/**
 * `ChatLevelConfigGate` — the L-9 blocking config-error card (ratified as
 * AMENDED, parent plan §3.6.0: the copy names the ACTUAL fault and points at
 * the surface that owns it — six strings, `lexide-p4-level-chip-miniplan.md`
 * §4-bis).
 *
 * ⚠ Fail direction (miniplan §3(a)): this gate borrows `ChatLLMKeysGate`'s
 * card SHAPE and inverts its POSTURE — it renders ONLY on a DEFINITIVE break
 * (`chatLevelGateInput` returns non-null). Loading, transient failure and
 * `unknown` must all fall through to a usable composer; the caller decides
 * mounting via `chatLevelGateInput`, this component just renders the card.
 */
import { AlertCircle } from "lucide-react";
import { useTranslation } from "react-i18next";

import { gateStringI18nKey, gateStringKey } from "@/lib/chat-level";

export function ChatLevelConfigGate({
  reason,
  detail,
}: {
  reason: string;
  detail: string;
}) {
  const { t } = useTranslation();
  const key = gateStringKey(reason, detail);

  return (
    <div
      data-testid="chat-level-config-gate"
      role="alert"
      className="mx-auto my-3 w-full max-w-xl rounded-lg border border-red-200 bg-red-50/70 p-3 dark:border-red-900/60 dark:bg-red-950/30"
    >
      <div className="flex items-start gap-2">
        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-600 dark:text-red-400" aria-hidden="true" />
        <div className="min-w-0">
          <p className="text-[13px] font-medium text-red-700 dark:text-red-300">
            {t("chat.level.gate.title")}
          </p>
          <p className="mt-0.5 text-[12px] leading-snug text-red-700/90 dark:text-red-300/90">
            {t(gateStringI18nKey(key))}
          </p>
          {/* The raw discriminator — an admin's breadcrumb, never hidden.
              For `unknownReason` it is the ONLY specific content. */}
          <p className="mt-1 truncate font-mono text-[10px] text-red-600/70 dark:text-red-400/60">
            {reason}
            {detail ? ` · ${detail}` : ""}
          </p>
        </div>
      </div>
    </div>
  );
}
